import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';

/** The hand-off's `‹ 1 2 ··· 98 99 ›`: two at each end, collapsing only when there is something to collapse. */
function pageWindow(total: number): (number | 'gap')[] {
	if (total <= 5) return Array.from({ length: total }, (_, index) => index);
	return [0, 1, 'gap', total - 2, total - 1];
}

function PageTab(props: { page: number; active: boolean; onClick: () => void; label: string }) {
	return (
		<button
			type="button"
			onClick={props.onClick}
			aria-label={props.label}
			aria-current={props.active ? 'page' : undefined}
			className={cn(
				'flex cursor-pointer items-center justify-center rounded-full px-[9px] py-2 text-sm leading-[1.5] transition-colors',
				props.active ? 'bg-ink text-white' : 'border-ink text-ink hover:bg-ink/5 border',
			)}
		>
			{/* So 1 and 99 make tabs of near-enough the same width. */}
			<span className="min-w-[18px] text-center">{props.page + 1}</span>
		</button>
	);
}

function Arrow(props: { label: string; disabled: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={props.onClick}
			disabled={props.disabled}
			aria-label={props.label}
			className="text-ink grid size-6 shrink-0 cursor-pointer place-items-center rounded-full bg-white transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-50"
		>
			{props.children}
		</button>
	);
}

export function NgoPagination(props: { page: number; pageCount: number; onChange: (page: number) => void }) {
	const { t } = useLingui();
	// lingui disallows arithmetic inside t``.
	const pageLabel = (index: number) => {
		const number = index + 1;
		return t`Go to page ${number}`;
	};
	if (props.pageCount <= 1) return null;

	return (
		<nav aria-label={t`Pages of partner NGOs`} className="mt-6 flex items-center justify-center gap-2">
			<Arrow label={t`Previous page`} disabled={props.page === 0} onClick={() => props.onChange(props.page - 1)}>
				<ArrowLeft className="size-3" />
			</Arrow>

			{pageWindow(props.pageCount).map((entry) =>
				entry === 'gap' ? (
					<span key="gap" aria-hidden className="text-ink/40 px-1 text-sm leading-none">
						···
					</span>
				) : (
					<PageTab
						key={entry}
						page={entry}
						active={entry === props.page}
						onClick={() => props.onChange(entry)}
						label={pageLabel(entry)}
					/>
				),
			)}

			<Arrow
				label={t`Next page`}
				disabled={props.page >= props.pageCount - 1}
				onClick={() => props.onChange(props.page + 1)}
			>
				<ArrowRight className="size-3" />
			</Arrow>
		</nav>
	);
}
